import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react"
import type { ItemExtraGroup, MenuItem } from "./types"
import { parseDecimal } from "./money"

/**
 * A pickup cart lives in the diner's browser and nowhere else until they pay.
 *
 * That is not a convenience — it is the invariant the whole pickup flow exists
 * to protect. The kitchen board filters on order status and never on payment
 * status, so an order that exists is an order somebody cooks. Dine-in is safe
 * structurally: the diner is at a table with an open tab. Pickup was not, because
 * browsing created orders for someone who could close the tab and walk away.
 *
 * So browsing writes nothing to the server. `Location::PickupMaterializationService`
 * turns this cart into orders when Stripe confirms, and never before.
 *
 * Keyed per restaurant, because pickup is addressable: a diner who opens one
 * place, changes their mind and opens another has two carts, and coming back to
 * the first finds it as they left it.
 */

const STORAGE_PREFIX = "menny.pickup.cart.v1"

export type PickupCartExtra = {
  id: number
  name: string
  price: string
}

export type PickupCartLine = {
  /** Stable within a cart. Two portions of the same dish with different sauces are two lines. */
  key: string
  itemId: number
  name: string
  quantity: number
  /** Dish only. The unit price the diner pays is this plus the extras. */
  basePrice: string
  currency: string
  notes: string | null
  extras: PickupCartExtra[]
}

export type PickupCartRefusal = {
  reason: string
  changes: { kind: string; itemId: number | null; name: string | null; price: string | null }[]
}

const storageKey = (slug: string) => `${STORAGE_PREFIX}.${slug}`

/**
 * What makes a line itself. Dish, chosen extras and the note: change any of them
 * and it is a different order, so it gets its own row rather than bumping the
 * quantity of one the diner asked for differently.
 */
function lineKey(itemId: number, extraIds: number[], notes: string | null) {
  return [itemId, [...extraIds].sort((a, b) => a - b).join("+"), notes ?? ""].join("|")
}

export function lineUnitPrice(line: PickupCartLine) {
  return line.extras.reduce((sum, extra) => sum + parseDecimal(extra.price), parseDecimal(line.basePrice))
}

export function lineTotal(line: PickupCartLine) {
  return lineUnitPrice(line) * line.quantity
}

export function cartTotal(lines: PickupCartLine[]) {
  return lines.reduce((sum, line) => sum + lineTotal(line), 0)
}

export function cartCount(lines: PickupCartLine[]) {
  return lines.reduce((sum, line) => sum + line.quantity, 0)
}

/**
 * The service fee a diner pays on a pickup order, from a percentage the server
 * sends as a string.
 *
 * Rounded to the cent through integers rather than by formatting, so it matches
 * what `User::PickupCheckoutService` computes with `(subtotal * rate / 100).round(2)`.
 * The screen and the charge disagreeing by a cent is the kind of thing a diner
 * notices and nobody can explain afterwards.
 *
 * Returns 0 for a missing, unparseable or non-positive rate. All three mean the
 * same thing to the screen — show no fee — and none of them should render "NaN"
 * at a diner about to pay.
 */
export function dinerServiceFee(subtotal: number, percentage: string | undefined) {
  const rate = Number.parseFloat(percentage ?? "")
  if (!Number.isFinite(rate) || rate <= 0) return 0

  return Math.round(subtotal * rate) / 100
}

/**
 * Anything unreadable is an empty cart. A cart is a convenience the diner can
 * rebuild in a minute; refusing to render the screen over a bad JSON blob — one
 * a browser extension or an older build could have left — would strand them on a
 * page with no way forward.
 */
function readCart(slug: string): PickupCartLine[] {
  if (typeof window === "undefined") return []

  try {
    const raw = window.localStorage.getItem(storageKey(slug))
    if (!raw) return []

    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []

    return parsed.filter(isCartLine)
  } catch {
    return []
  }
}

function isCartLine(value: unknown): value is PickupCartLine {
  if (typeof value !== "object" || value === null) return false
  const line = value as Partial<PickupCartLine>

  return (
    typeof line.key === "string" &&
    typeof line.itemId === "number" &&
    typeof line.quantity === "number" &&
    line.quantity > 0 &&
    typeof line.basePrice === "string" &&
    Array.isArray(line.extras)
  )
}

function writeCart(slug: string, lines: PickupCartLine[]) {
  if (typeof window === "undefined") return

  try {
    if (lines.length === 0) {
      window.localStorage.removeItem(storageKey(slug))
      return
    }
    window.localStorage.setItem(storageKey(slug), JSON.stringify(lines))
  } catch {
    // A full or disabled localStorage must not take the menu down with it. The
    // cart stays in React state for this page; it just will not survive a reload.
  }
}

export type PickupCartApi = {
  slug: string
  lines: PickupCartLine[]
  count: number
  total: number
  add: (item: MenuItem, options: { quantity: number; extraIds: number[]; notes: string | null }) => void
  setQuantity: (key: string, quantity: number) => void
  remove: (key: string) => void
  clear: () => void
  /** Replaces the cart with the one the server corrected, after a refused checkout. */
  replaceWith: (lines: PickupCartLine[]) => void
}

const PickupCartContext = createContext<PickupCartApi | null>(null)

/**
 * Null on every dine-in screen, which is how the shared dish components tell the
 * two apart: with a cart in context a dish goes into the browser, without one it
 * POSTs to `/orders` against the visit's open bill.
 */
export function usePickupCart() {
  return useContext(PickupCartContext)
}

export function PickupCartProvider({ slug, children }: { slug: string; children: React.ReactNode }) {
  const [lines, setLines] = useState<PickupCartLine[]>(() => readCart(slug))

  // Re-read when the diner navigates to a different restaurant: the provider is
  // mounted by the layout and survives the page swap.
  useEffect(() => {
    setLines(readCart(slug))
  }, [slug])

  // Two tabs open on the same restaurant is a normal thing to do, and a cart
  // that disagrees with itself across them is how a diner pays for one of the
  // two. `storage` only fires in the *other* tabs, so this cannot loop.
  useEffect(() => {
    if (typeof window === "undefined") return

    const onStorage = (event: StorageEvent) => {
      if (event.key !== storageKey(slug)) return
      setLines(readCart(slug))
    }

    window.addEventListener("storage", onStorage)
    return () => window.removeEventListener("storage", onStorage)
  }, [slug])

  const commit = useCallback(
    (next: PickupCartLine[]) => {
      setLines(next)
      writeCart(slug, next)
    },
    [slug]
  )

  const api = useMemo<PickupCartApi>(() => {
    return {
      slug,
      lines,
      count: cartCount(lines),
      total: cartTotal(lines),

      add: (item, { quantity, extraIds, notes }) => {
        const extras = extrasFor(item.extraGroups ?? [], extraIds)
        const key = lineKey(item.id, extraIds, notes)
        const existing = lines.find((line) => line.key === key)

        // The same dish, the same extras, the same note: one row with a bigger
        // number, not two identical rows the diner has to reconcile by eye.
        const next = existing
          ? lines.map((line) =>
              line.key === key ? { ...line, quantity: line.quantity + quantity } : line
            )
          : [
              ...lines,
              {
                key,
                itemId: item.id,
                name: item.name,
                quantity,
                basePrice: item.price,
                currency: item.currency,
                notes,
                extras,
              },
            ]

        commit(next)
      },

      setQuantity: (key, quantity) => {
        if (quantity < 1) {
          commit(lines.filter((line) => line.key !== key))
          return
        }
        commit(lines.map((line) => (line.key === key ? { ...line, quantity } : line)))
      },

      remove: (key) => commit(lines.filter((line) => line.key !== key)),
      clear: () => commit([]),
      replaceWith: (next) => commit(next),
    }
  }, [slug, lines, commit])

  return <PickupCartContext.Provider value={api}>{children}</PickupCartContext.Provider>
}

function extrasFor(groups: ItemExtraGroup[], extraIds: number[]): PickupCartExtra[] {
  const chosen = new Set(extraIds)

  return groups
    .flatMap((group) => group.extras)
    .filter((extra) => chosen.has(extra.id))
    .map((extra) => ({ id: extra.id, name: extra.name, price: extra.price }))
}

/**
 * The wire shape the checkout endpoint reads. Prices travel so the server can
 * tell the diner *what changed* — `User::PickupCartValidationService` re-reads
 * every one of them from the item and never trusts these.
 */
export function cartPayload(lines: PickupCartLine[]) {
  return lines.map((line) => ({
    item_id: line.itemId,
    quantity: line.quantity,
    price: lineUnitPrice(line).toFixed(2),
    notes: line.notes,
    extra_ids: line.extras.map((extra) => extra.id),
  }))
}

/**
 * A stable string that changes exactly when the cart the server would price
 * changes.
 *
 * Derived from `cartPayload` rather than from the lines, so it moves on the
 * facts the server reads and on nothing else: renaming a dish in the menu does
 * not invalidate a quote, changing a quantity does. `JSON.stringify` over an
 * array built field-by-field is order-stable here because `cartPayload` writes
 * the same keys in the same sequence for every line.
 */
export function cartPayloadSignature(lines: PickupCartLine[]) {
  return JSON.stringify(cartPayload(lines))
}

/**
 * Rebuilds the cart from the corrected one the server returned with a refusal,
 * so the sold-out dish is gone and the moved price is right before the diner
 * taps pay again. Server field names, because this is the server's answer.
 */
export function cartFromServer(
  serverLines: {
    item_id: number
    name: string
    quantity: number
    base_price: string
    currency: string
    notes: string | null
    modifiers: { id: number; name: string; price: string }[]
  }[]
): PickupCartLine[] {
  return serverLines.map((line) => {
    const extras = (line.modifiers ?? []).map((modifier) => ({
      id: modifier.id,
      name: modifier.name,
      price: String(modifier.price),
    }))

    return {
      key: lineKey(line.item_id, extras.map((extra) => extra.id), line.notes ?? null),
      itemId: line.item_id,
      name: line.name,
      quantity: line.quantity,
      basePrice: String(line.base_price),
      currency: line.currency,
      notes: line.notes ?? null,
      extras,
    }
  })
}
